import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { Theme, ThemeColors } from '@/themes/types';
import { themes, getThemeById } from '@/themes';

interface ThemeContextType {
  currentTheme: Theme;
  setTheme: (themeId: string) => void;
  themes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const THEME_STORAGE_KEY = 'SEO_THEME';

const camelToKebab = (str: string): string =>
  str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

const mapToShadcnVariables = (colors: ThemeColors): Record<string, string> => ({
  '--background': colors.bgPrimary,
  '--foreground': colors.textPrimary,
  '--card': colors.bgSecondary,
  '--card-foreground': colors.textPrimary,
  '--popover': colors.bgSecondary,
  '--popover-foreground': colors.textPrimary,
  '--primary': colors.accentPrimary,
  '--primary-foreground': colors.textInverse,
  '--secondary': colors.bgTertiary,
  '--secondary-foreground': colors.textSecondary,
  '--muted': colors.bgTertiary,
  '--muted-foreground': colors.textMuted,
  '--accent': colors.bgAccent,
  '--accent-foreground': colors.textPrimary,
  '--destructive': colors.error,
  '--border': colors.borderPrimary,
  '--input': colors.borderPrimary,
  '--ring': colors.accentPrimary,
  '--sidebar': colors.sidebarBg,
  '--sidebar-foreground': colors.sidebarText,
  '--sidebar-primary': colors.accentPrimary,
  '--sidebar-primary-foreground': colors.textInverse,
  '--sidebar-accent': colors.sidebarHover,
  '--sidebar-accent-foreground': colors.sidebarText,
  '--sidebar-border': colors.borderPrimary,
  '--sidebar-ring': colors.accentPrimary,
});

const applyThemeToDOM = (theme: Theme) => {
  const root = document.documentElement;

  // Ustaw custom CSS variables z motywu
  Object.entries(theme.colors).forEach(([key, value]) => {
    if (value !== undefined) {
      root.style.setProperty(`--${camelToKebab(key)}`, value);
    }
  });

  // Mapuj na zmienne shadcn
  const shadcnVars = mapToShadcnVariables(theme.colors);
  Object.entries(shadcnVars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  // Klasy efektow
  root.classList.remove('theme-glass', 'theme-gradient', 'theme-minimal', 'theme-dark');
  if (theme.effects?.glassmorphism) root.classList.add('theme-glass');
  if (theme.effects?.gradients) root.classList.add('theme-gradient');
  if (theme.id === 'dark') root.classList.add('theme-dark');

  // Scrollbar
  updateScrollbarStyles(theme);
};

const updateScrollbarStyles = (theme: Theme) => {
  const styleId = 'theme-scrollbar-styles';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: ${theme.colors.scrollbarTrack}; }
    ::-webkit-scrollbar-thumb { background: ${theme.colors.scrollbarThumb}; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: ${theme.colors.scrollbarThumbHover}; }
  `;
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      if (saved) return getThemeById(saved);
    }
    return getThemeById('glass');
  });

  useEffect(() => {
    applyThemeToDOM(currentTheme);
    localStorage.setItem(THEME_STORAGE_KEY, currentTheme.id);
  }, [currentTheme]);

  // Aplikuj motyw przy pierwszym renderze
  useEffect(() => {
    applyThemeToDOM(currentTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTheme = (themeId: string) => setCurrentTheme(getThemeById(themeId));

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme musi być użyty wewnątrz ThemeProvider');
  }
  return context;
}
